import React from 'react'
import Box from '@mui/material/Box'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { Link, useLocation } from 'react-router-dom'

import { getEntryById } from '../../navigation/registry'
import { getEntryByPath } from '../../navigation'
import { getEntryPlacement } from '../../navigation/sections'
import { getEntryAccess } from '../../navigation/visibility'
import { useNavContext } from '../../navigation/useNavigation'

/**
 * Shared page header: breadcrumb trail, title, related-page chips and an
 * actions slot. Everything but the title is derived from the navigation
 * registry (src/navigation/registry.js), so pages don't hand-maintain their
 * own idea of where they live or what they relate to.
 *
 * Converting a page:
 *   1. Delete its hand-rolled title Typography.
 *   2. Render <PageHeader actions={<its buttons />} /> at the top. The
 *      registry entry is matched from the URL; pass `registryId` only when
 *      the match is ambiguous.
 *   3. On a detail page, pass `title` (the record's name) and `crumbs` for
 *      the extra level, e.g. crumbs={[{ label: 'Vehicles', to: '/fleet' }]}.
 *
 * Related chips come from the entry's `related` list, filtered to what this
 * user can access - add relationships in the registry, not here.
 */
const PageHeader = ({ registryId, title, crumbs = [], actions, children }) => {
  const location = useLocation()
  const ctx = useNavContext()

  const entry = registryId
    ? getEntryById(registryId)
    : getEntryByPath(location.pathname)

  const { section, group } = entry
    ? getEntryPlacement(entry)
    : { section: null, group: null }

  const related = (entry?.related ?? [])
    .map((id) => getEntryById(id))
    .filter(Boolean)
    .filter((relatedEntry) => getEntryAccess(relatedEntry, ctx) === 'visible')

  const pageTitle = title ?? entry?.title

  return (
    <Box>
      {(section || crumbs.length > 0) && (
        <Breadcrumbs
          separator={<NavigateNextIcon sx={{ fontSize: 14 }} />}
          sx={{
            mb: 0.5,
            '& .MuiBreadcrumbs-li': { fontSize: '0.75rem' },
            color: 'text.secondary',
          }}
        >
          {section && (
            <Typography
              component={Link}
              to={`/sitemap#${section.id}`}
              fontSize="0.75rem"
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {section.title}
            </Typography>
          )}
          {group && (
            <Typography fontSize="0.75rem" color="inherit">
              {group.title}
            </Typography>
          )}
          {crumbs.map((crumb) => (
            <Typography
              key={crumb.to ?? crumb.label}
              component={crumb.to ? Link : 'span'}
              to={crumb.to}
              fontSize="0.75rem"
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                '&:hover': crumb.to ? { textDecoration: 'underline' } : {},
              }}
            >
              {crumb.label}
            </Typography>
          ))}
          {pageTitle && (
            <Typography fontSize="0.75rem" color="text.primary">
              {pageTitle}
            </Typography>
          )}
        </Breadcrumbs>
      )}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="title2">{pageTitle}</Typography>
        {actions && (
          <Stack direction="row" alignItems="center" gap={1}>
            {actions}
          </Stack>
        )}
      </Stack>

      {related.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1}>
          {related.map((relatedEntry) => (
            <Chip
              key={relatedEntry.id}
              label={relatedEntry.title}
              size="small"
              clickable
              component={Link}
              to={relatedEntry.path}
              sx={{
                borderRadius: '999px',
                fontSize: '0.7rem',
                height: 22,
                fontWeight: 600,
                backgroundColor: 'anMuted.main',
                '&:hover': { backgroundColor: 'greycontrast.main' },
              }}
            />
          ))}
        </Stack>
      )}

      {children}
    </Box>
  )
}

export default PageHeader
